import { useState, useMemo } from 'react';
import {
  useGetMemberSummaryQuery,
  useGetSinglesStrokeResultsQuery,
  useGetLatestRyderCupTeamsQuery,
} from '../state/api';
import { selectAllMembers, selectMemberEntities } from '../state/entitySelectors';
import { useAppSelector } from '../hooks/reduxHooks';
import HeadToHead from './head-to-head';
import {
  getFilteredOpponentIds,
  getSortedMembers,
  getFilteredResults,
  sortMatchResultsByYear,
} from './head-to-head.utils';
import { formatMemberName } from '../utils/members';
import type { TeamMatchPlayResults } from '../types/api.types';

/**
 * Transforms singles stroke results into a nested map structure
 * Duplicates results with reversed teams for bidirectional lookup
 */
const createMatchResultsMap = (singlesStrokeResults: TeamMatchPlayResults[]) => {
  // First, duplicate the results with reversed teams
  const duplicatedResults = [
    ...singlesStrokeResults,
    ...singlesStrokeResults.map((obj) => ({
      ...obj,
      teamAMember1: obj.teamBMember1,
      teamBMember1: obj.teamAMember1,
      teamA: obj.teamB,
      teamB: obj.teamA,
      shotsGivenByTeamB: -(obj.shotsGivenByTeamB ?? 0),
    })),
  ];

  // Then create the nested map structure
  return duplicatedResults.reduce(
    (map, obj) => ({
      ...map,
      [obj.teamAMember1!]: {
        ...map[obj.teamAMember1!],
        [obj.teamBMember1!]: [...(map[obj.teamAMember1!]?.[obj.teamBMember1!] || []), obj],
      },
    }),
    {} as Record<string | number, Record<string | number, typeof singlesStrokeResults>>
  );
};

const HeadToHeadContainer = () => {
  // State management for member and opponent selection
  const [selectedMember, setSelectedMember] = useState<string | number | null>(null);
  const [selectedOpponent, setSelectedOpponent] = useState<string | number | null>(null);
  const [showLatestRyderCupOnly, setShowLatestRyderCupOnly] = useState(false);

  // Fetch data with RTK Query
  useGetMemberSummaryQuery();

  // Use entity selector for optimized access to normalized data
  const lstMembers = useAppSelector(selectAllMembers);
  const memberEntities = useAppSelector(selectMemberEntities);
  const { data: singlesStrokeResults = [] } = useGetSinglesStrokeResultsQuery();
  const { data: latestRyderCupTeams = [] } = useGetLatestRyderCupTeamsQuery();

  // Transform the array into a nested map structure matching the old slice format
  // This duplicates results with reversed teams and creates a nested object: map[member1Id][member2Id] = [matches]
  const mapSinglesStrokeResults = useMemo(() => createMatchResultsMap(singlesStrokeResults), [singlesStrokeResults]);

  // Calculate derived data for selected member
  const selectedMemberDetails = selectedMember != null ? memberEntities[selectedMember] : undefined;
  const selectedMemberName = selectedMemberDetails ? formatMemberName(selectedMemberDetails) : '?';
  const player1Results = selectedMember != null ? mapSinglesStrokeResults?.[selectedMember] : undefined;

  // Get all opponent IDs sorted by number of matches
  const allOpponentIds = player1Results
    ? Object.keys(player1Results).sort((a, b) => player1Results[b].length - player1Results[a].length)
    : [];

  // Filter opponents by opposing team when Ryder Cup filter is active
  const opponentIds = getFilteredOpponentIds({
    allOpponentIds,
    showLatestRyderCupOnly,
    selectedMemberId: selectedMember,
    latestRyderCupTeams,
    player1Results,
  });

  // Handler for member selection (clears opponent when changing member)
  const handleMemberSelection = (id: string | number | null) => {
    setSelectedMember(id);
    setSelectedOpponent(null);
  };

  // Create a Set of Ryder Cup member IDs for efficient lookup
  const latestRyderCupMemberIds = useMemo(() => new Set(latestRyderCupTeams.map((m) => m.id)), [latestRyderCupTeams]);

  // Sort members by match count
  const sortedMembers = useMemo(
    () => getSortedMembers(lstMembers, showLatestRyderCupOnly, latestRyderCupTeams, mapSinglesStrokeResults),
    [lstMembers, showLatestRyderCupOnly, latestRyderCupTeams, mapSinglesStrokeResults]
  );

  // Filter members based on Ryder Cup checkbox
  const filteredMembers = useMemo(
    () =>
      showLatestRyderCupOnly ? sortedMembers.filter((member) => latestRyderCupMemberIds.has(member.id)) : sortedMembers,
    [showLatestRyderCupOnly, sortedMembers, latestRyderCupMemberIds]
  );

  // Pre-process match results for results history panel
  const sortedMatchResults = useMemo(() => {
    const filtered = getFilteredResults(selectedMember, selectedOpponent, player1Results);
    return filtered ? sortMatchResultsByYear(filtered) : undefined;
  }, [selectedMember, selectedOpponent, player1Results]);

  return (
    <HeadToHead
      lstMembers={filteredMembers}
      memberEntities={memberEntities}
      mapSinglesStrokeResults={mapSinglesStrokeResults}
      latestRyderCupTeams={latestRyderCupTeams}
      selectedMember={selectedMember}
      selectedOpponent={selectedOpponent}
      showLatestRyderCupOnly={showLatestRyderCupOnly}
      selectedMemberName={selectedMemberName}
      player1Results={player1Results}
      opponentIds={opponentIds}
      latestRyderCupMemberIds={latestRyderCupMemberIds}
      sortedMatchResults={sortedMatchResults}
      onMemberSelect={handleMemberSelection}
      onOpponentSelect={setSelectedOpponent}
      onFilterChange={setShowLatestRyderCupOnly}
    />
  );
};

export default HeadToHeadContainer;
